'use client';

import { useState, useCallback, useEffect, useId, JSX } from 'react';
import ImageWrapper from '@/helpers/Wrappers/ImageWrapper/ImageWrapper';
import { toEnhancedImage } from '@/utils/image-utils';
import { ILearningMaterials } from '@/.generated';
import { learningMaterialCardVariants } from './LearningMaterials.styles';
import { getCSLPAttributes } from '@/utils/type-guards';
import RichTextWrapper from '@/helpers/Wrappers/RichTextWrapper/RichTextWrapper';
import { ButtonWrapper } from '@/helpers/Wrappers/ButtonWrapper/ButtonWrapper';
import { DownloadLink } from '@/helpers/Wrappers/DownloadLink/DownloadLink';
import { useCart } from './CartProvider';

// Drill into the nested CMS structure to extract a single card's shape
type MaterialCard = NonNullable<
  NonNullable<
    NonNullable<ILearningMaterials['material_groups']>[number]['material_types']
  >[number]['material_cards']
>[number];

// Extend with label strings that come from the parent ILearningMaterials.labels block
export type LearningMaterialCardProps = MaterialCard & {
  quantityLabel?: string;
  addToCartLabel?: string;
  downloadLabel?: string;
};

export const LearningMaterialCard = ({
  header,
  title,
  label,
  description,
  thumbnail,
  hide_add_to_cart,
  material_file,
  quantityLabel = 'QTY:',
  addToCartLabel = 'Add to bag',
  downloadLabel = 'Free download',
  $: cslp,
}: LearningMaterialCardProps): JSX.Element => {
  const [quantity, setQuantity] = useState(0);
  // Set when the user attempts to add the item without entering a quantity
  const [showZeroError, setShowZeroError] = useState(false);
  const inputId = useId();
  const errorId = useId();
  const { items, addItem, maxAllowedQuantity, maxQuantityErrorMessage, zeroQuantityErrorMessage } =
    useCart();

  const styles = learningMaterialCardVariants();

  // True when the entered quantity is above the CMS-configured per-item ceiling
  const isOverLimit = quantity > maxAllowedQuantity;

  // True when either validation rule is currently failing (the two are mutually exclusive)
  const hasError = isOverLimit || showZeroError;

  // Current quantity of this material in the bag (undefined when not added yet)
  const cartQuantity = items.find((line) => line.id === title)?.quantity;

  // Nothing to commit when the entered quantity already matches what's in the bag
  const matchesCartQuantity = quantity === cartQuantity;

  // Mirror the bag back into the input so summary edits and removals stay in sync
  useEffect(() => {
    setQuantity(cartQuantity ?? 0);
  }, [cartQuantity]);

  // Allow only digits; empty input resets to 0 (over-limit values are kept so the user sees their input)
  const handleQuantityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Clear the zero-quantity prompt as soon as the user edits the field
    setShowZeroError(false);
    const raw = e.target.value;
    if (raw === '') {
      setQuantity(0);
      return;
    }
    if (!/^\d+$/.test(raw)) return;
    setQuantity(parseInt(raw, 10));
  }, []);

  // Commit the chosen quantity to the bag — prompt on empty, block over-limit values
  const handleAddToCart = useCallback(() => {
    if (quantity < 1) {
      setShowZeroError(true);
      return;
    }
    if (quantity > maxAllowedQuantity) return;
    addItem({ id: title, title }, quantity);
  }, [quantity, maxAllowedQuantity, title, addItem]);

  // Saved file name + analytics label shared by the download link
  const downloadFileName = material_file?.filename || material_file?.title;

  // Icon + label content shared between the active link and the disabled (no-file) button
  const downloadContent = (
    <>
      <i className="fa-sharp fa-solid fa-arrow-down-to-bracket" aria-hidden="true" />
      {downloadLabel}
    </>
  );

  return (
    <article className={styles.card()}>
      {header && (
        <div className={styles.headerBadge()} {...getCSLPAttributes(cslp?.header)}>
          {header}
        </div>
      )}
      <div className={styles.contentWrapper()}>
        <div className={styles.textContent()}>
          <RichTextWrapper
            content={title}
            className={styles.title()}
            {...getCSLPAttributes(cslp?.title)}
          />
          {label && (
            <span className={styles.label()} {...getCSLPAttributes(cslp?.label)}>
              {label}
            </span>
          )}
          {description && (
            <RichTextWrapper
              content={description}
              className={styles.description()}
              {...getCSLPAttributes(cslp?.description)}
            />
          )}
        </div>
        {thumbnail?.url && (
          <div className={styles.thumbnailWrapper()}>
            <ImageWrapper
              image={toEnhancedImage(thumbnail, {
                alternateText: thumbnail.title || title,
                fallbackWidth: 128,
                fallbackHeight: 160,
                cslp: cslp?.thumbnail,
              })}
              wrapperClassName="w-32 h-auto"
              imageClassName={styles.thumbnail()}
            />
          </div>
        )}
      </div>
      <div className={styles.actionsWrapper()}>
        {!hide_add_to_cart ? (
          <>
            <div className={styles.quantityWrapper()}>
              <label htmlFor={inputId} className={styles.quantityLabel()}>
                {quantityLabel}
              </label>
              <input
                id={inputId}
                type="text"
                inputMode="numeric"
                value={quantity}
                onChange={handleQuantityChange}
                className={styles.quantityInput({ error: isOverLimit })}
                aria-label={`Quantity for ${title}`}
                aria-invalid={hasError}
                aria-describedby={hasError ? errorId : undefined}
                max={maxAllowedQuantity}
              />
            </div>
            <ButtonWrapper
              className={styles.addToCartButton()}
              onClick={handleAddToCart}
              aria-label={`${addToCartLabel} - ${title}`}
              disabled={isOverLimit || matchesCartQuantity}
              clickName={addToCartLabel}
              clickLocation={`LearningMaterials-Card`}
            >
              {addToCartLabel}
            </ButtonWrapper>
            {hasError && (
              <p id={errorId} role="alert" className={styles.quantityError()}>
                {isOverLimit ? maxQuantityErrorMessage : zeroQuantityErrorMessage}
              </p>
            )}
          </>
        ) : material_file?.url ? (
          <DownloadLink
            href={material_file.url}
            forceDownload
            clickName={title}
            documentName={downloadFileName}
            downloadFileName={downloadFileName}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.downloadButton()}
            aria-label={`${downloadLabel} - ${title}`}
          >
            {downloadContent}
          </DownloadLink>
        ) : (
          <button
            type="button"
            className={styles.downloadButton()}
            aria-label={`${downloadLabel} - ${title}`}
            disabled
          >
            {downloadContent}
          </button>
        )}
      </div>
    </article>
  );
};
